/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { EuiBadge, EuiFieldNumber, EuiFieldText, EuiFormRow, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { BudgetConfigurationValues } from '../../../types'

type Props = {
  values: BudgetConfigurationValues
  errors: Partial<Record<keyof BudgetConfigurationValues, string>>
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isHidden: boolean
  isProcessing: boolean
  amountRef: React.RefObject<HTMLInputElement>
  nameRef: React.RefObject<HTMLInputElement>
}

const Configuration: FunctionComponent<Props> = ({
  values,
  errors,
  handleChange,
  isHidden,
  isProcessing,
  amountRef,
  nameRef,
}) => {
  const { formatMessage } = useIntl()

  const hasNameErrors = !!errors.name
  const hasAmountErrors = !!errors.amount

  const handleAmountKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if ([',', '.', '-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault()
    }
  }

  if (isHidden) {
    return (
      <EuiText size='s' color='subdued'>
        <FormattedMessage
          id='billing.notifications.budgets.budget-settings.configuration.hidden'
          defaultMessage='Set budget name, time range, and target amount.'
        />
      </EuiText>
    )
  }

  return (
    <Fragment>
      <EuiFormRow
        label={formatMessage({
          id: 'billing.notifications.budgets.configuration.name.label',
          defaultMessage: 'Name',
        })}
        helpText={formatMessage({
          id: 'billing.notifications.budgets.configuration.name.help',
          defaultMessage: 'Give your budget a descriptive name.',
        })}
        isDisabled={isProcessing}
        isInvalid={hasNameErrors}
        error={errors.name}
      >
        <EuiFieldText
          inputRef={nameRef}
          name='name'
          disabled={isProcessing}
          isInvalid={hasNameErrors}
          isLoading={isProcessing}
          onChange={handleChange}
          placeholder={formatMessage({
            id: 'billing.notifications.budgets.configuration.name.placeholder',
            defaultMessage: 'e.g., Monthly Organization Budget',
          })}
          value={values.name}
        />
      </EuiFormRow>

      <EuiFormRow
        label={formatMessage({
          id: 'billing.notifications.budgets.configuration.time-range.label',
          defaultMessage: 'Time range',
        })}
        helpText={formatMessage({
          id: 'billing.notifications.budgets.configuration.time-range.help',
          defaultMessage: 'Your budget resets on the first day of each calendar month.',
        })}
        isDisabled={isProcessing}
      >
        <EuiBadge color='default'>
          <FormattedMessage
            id='billing.notifications.budgets.configuration.time-range.monthly'
            defaultMessage='Monthly'
          />
        </EuiBadge>
      </EuiFormRow>

      <EuiFormRow
        label={formatMessage({
          id: 'billing.notifications.budgets.configuration.amount.label',
          defaultMessage: 'Target amount (ECU)',
        })}
        helpText={formatMessage({
          id: 'billing.notifications.budgets.configuration.amount.help',
          defaultMessage:
            'Track your actual spend against the target amount. Costs are updated within 24 hours.',
        })}
        isDisabled={isProcessing}
        isInvalid={hasAmountErrors}
        error={errors.amount}
      >
        <EuiFieldNumber
          inputRef={amountRef}
          name='amount'
          type='number'
          inputMode='numeric'
          pattern='[0-9]*'
          step={1}
          min={1}
          disabled={isProcessing}
          isInvalid={hasAmountErrors}
          isLoading={isProcessing}
          onChange={handleChange}
          onKeyDown={handleAmountKeyDown}
          placeholder={formatMessage({
            id: 'billing.notifications.budgets.configuration.amount.placeholder',
            defaultMessage: 'Type amount',
          })}
          value={values.amount}
        />
      </EuiFormRow>
    </Fragment>
  )
}

export default Configuration
